

// Not show varient until first click
if (!customElements.get('media-gallery')) {
  customElements.define('media-gallery', class MediaGallery extends HTMLElement {
    constructor() {
      super();

      // Safely initialize elements with null checks
      this.elements = {
        liveRegion: this.querySelector('[id^="GalleryStatus"]'),
        viewer: this.querySelector('[id^="GalleryViewer"]'),
        thumbnails: this.querySelector('[id^="GalleryThumbnails"]')
      };
      
      this.mql = window.matchMedia('(min-width: 750px)');
      
      // Only initialize if required elements exist
      if (!this.elements.viewer || !this.elements.thumbnails) {
        console.warn('MediaGallery: Required elements not found');
        return;
      }

      this.initializeGallery();
      this.setupThumbnailEvents();
      
      if (this.dataset.desktopLayout === 'thumbnail_slider' && this.mql.matches) {
        this.removeListSemantic();
      }
    }

    initializeGallery() {
      // Safely remove active classes
      this.safeRemoveActiveClasses(this.elements.viewer, '[data-media-id]');
      this.safeRemoveActiveClasses(this.elements.thumbnails, '[id^="Slide-"]');

      // Activate first non-variant media
      const firstMedia = this.elements.viewer.querySelector('[data-media-id]:not([data-variant-id])');
      if (firstMedia) {
        this.safeActivateMedia(firstMedia.dataset.mediaId);
      }
    }

    safeRemoveActiveClasses(container, selector) {
      if (!container) return;
      
      const elements = container.querySelectorAll(selector);
      elements.forEach(element => {
        try {
          element?.classList?.remove('is-active');
          const button = element?.querySelector('button');
          if (button) {
            button.removeAttribute('aria-current');
          }
        } catch (e) {
          console.error('Error removing active classes:', e);
        }
      });
    }

    setupThumbnailEvents() {
      const mediaToSwitch = this.elements.thumbnails.querySelectorAll('[data-target]');
      mediaToSwitch.forEach(item => {
        const button = item.querySelector('button');
        if (!button) return;
        
        button.addEventListener('click', () => {
          this.setActiveMedia(item.dataset.target, false, true);
        });
        button.addEventListener('dblclick', () => {
          this.setActiveMedia(item.dataset.target, false, true);
        });
      });
    }

    setActiveMedia(mediaId, prepend = false, scrollIntoView = true) {
      if (!mediaId) {
        console.error('setActiveMedia: No mediaId provided');
        return;
      }

      const activeMedia = this.elements.viewer?.querySelector(`[data-media-id="${mediaId}"]`);
      if (!activeMedia) {
        console.error(`setActiveMedia: Media not found for id ${mediaId}`);
        return;
      }

      // Safely update active media
      this.safeRemoveActiveClasses(this.elements.viewer, '[data-media-id]');
      activeMedia.classList.add('is-active');

      if (prepend) {
        this.prependActiveElements(activeMedia, mediaId);
      }

      this.preventStickyHeader();
      this.scheduleScroll(activeMedia, scrollIntoView);
      this.playActiveMedia(activeMedia);
      
      const activeThumbnail = this.elements.thumbnails?.querySelector(`[data-target="${mediaId}"]`);
      if (activeThumbnail) {
        this.setActiveThumbnail(activeThumbnail);
        this.announceLiveRegion(activeMedia, activeThumbnail.dataset.mediaPosition);
      }

      this.updateVariantFromMedia(mediaId);
    }

    prependActiveElements(activeMedia, mediaId) {
      try {
        activeMedia.parentElement.prepend(activeMedia);
        
        const activeThumbnail = this.elements.thumbnails?.querySelector(`[data-target="${mediaId}"]`);
        if (activeThumbnail) {
          activeThumbnail.parentElement.prepend(activeThumbnail);
        }

        if (this.elements.viewer.slider) {
          this.elements.viewer.resetPages();
        }
      } catch (e) {
        console.error('Error prepending active elements:', e);
      }
    }

    scheduleScroll(activeMedia, scrollIntoView) {
      window.setTimeout(() => {
        try {
          if (this.elements.thumbnails) {
            activeMedia.parentElement?.scrollTo?.({ left: activeMedia.offsetLeft });
          }
          if ((!this.elements.thumbnails || this.dataset.desktopLayout === 'stacked') && scrollIntoView) {
            activeMedia.scrollIntoView({ behavior: 'smooth' });
          }
        } catch (e) {
          console.error('Error during scroll:', e);
        }
      }, 10);
    }

    setActiveThumbnail(thumbnail) {
      if (!this.elements.thumbnails || !thumbnail) return;

      try {
        this.safeRemoveActiveClasses(this.elements.thumbnails, '[id^="Slide-"]');
        
        thumbnail.classList.add('is-active');
        const button = thumbnail.querySelector('button');
        if (button) {
          button.setAttribute('aria-current', 'true');
        }

        if (!this.elements.thumbnails.isSlideVisible(thumbnail, 10)) {
          this.elements.thumbnails.slider?.scrollTo?.({ left: thumbnail.offsetLeft });
        }
      } catch (e) {
        console.error('Error setting active thumbnail:', e);
      }
    }

    updateVariantFromMedia(mediaId) {
      try {
        const productJson = document.getElementById('ProductJson');
        if (!productJson) return;

        const product = JSON.parse(productJson.textContent);
        const variant = product.variants.find(v => v.featured_media?.id == mediaId);
        if (!variant) return;

        const variantInput = document.querySelector(`input[name="id"][value="${variant.id}"]`);
        variantInput?.click();
      } catch (e) {
        console.error('Error updating variant from media:', e);
      }
    }

    announceLiveRegion(activeItem, position) {
      const image = activeItem.querySelector('.product__modal-opener--image img');
      if (!image || !this.elements.liveRegion) return;

      image.onload = () => {
        try {
          this.elements.liveRegion.setAttribute('aria-hidden', 'false');
          this.elements.liveRegion.innerHTML = 
            theme.accessibilityStrings?.imageAvailable?.replace('[index]', position) || '';
          
          setTimeout(() => {
            this.elements.liveRegion.setAttribute('aria-hidden', 'true');
          }, 2000);
        } catch (e) {
          console.error('Error announcing live region:', e);
        }
      };
      image.src = image.src;
    }

    playActiveMedia(activeItem) {
      try {
        window.pauseAllMedia?.();
        const deferredMedia = activeItem.querySelector('.deferred-media');
        deferredMedia?.loadContent?.(false);
      } catch (e) {
        console.error('Error playing active media:', e);
      }
    }

    preventStickyHeader() {
      try {
        this.stickyHeader = this.stickyHeader || document.querySelector('sticky-header');
        this.stickyHeader?.dispatchEvent(new Event('preventHeaderReveal'));
      } catch (e) {
        console.error('Error preventing sticky header:', e);
      }
    }

    removeListSemantic() {
      try {
        if (!this.elements.viewer.slider) return;
        this.elements.viewer.slider.setAttribute('role', 'presentation');
        this.elements.viewer.sliderItems?.forEach?.(slide => {
          slide.setAttribute('role', 'presentation');
        });
      } catch (e) {
        console.error('Error removing list semantic:', e);
      }
    }

    safeActivateMedia(mediaId) {
      try {
        const media = this.elements.viewer.querySelector(`[data-media-id="${mediaId}"]`);
        if (media) {
          media.classList.add('is-active');
          
          const thumbnail = this.elements.thumbnails.querySelector(`[data-target="${mediaId}"]`);
          if (thumbnail) {
            thumbnail.classList.add('is-active');
            const button = thumbnail.querySelector('button');
            if (button) {
              button.setAttribute('aria-current', 'true');
            }
          }
        }
      } catch (e) {
        console.error('Error activating media:', e);
      }
    }
  });
}
